/**
 * Create an ámbito.
 *
 * THE ID IS A FILENAME AND THE CASE IS PART OF IT
 * ----------------------------------------------
 * `ck_scopes_id_shape` is `^[a-z][a-z0-9_-]{1,40}$`, and the runbook is
 * explicit that `us-amazon` and `us_amazon` are two DIFFERENT scopes on
 * purpose, because the id IS the filename
 * (`scopes/{project}/{scope}.duckdb`). The decisions log records the
 * matching rule on the binder: `set_current_scope` refuses the wrong
 * case rather than folding it.
 *
 * So this form validates client-side — the `KEY_RE` pattern in
 * `DimensionFormModal` is the precedent — to turn a Postgres CHECK
 * violation into a sentence, and it **never lowercases what the analyst
 * typed**. Silently normalising here would create a scope under a name
 * they did not choose and then bind nobody to it.
 *
 * CREATING ONE GRANTS NOBODY ANYTHING
 * -----------------------------------
 * A new scope has no group, no member and no file until something
 * builds it. The modal says so rather than implying the work is done,
 * and it lands the analyst on the detail page, which is where the
 * projects and the rebuild live.
 */

import { Button, Input, Modal, Textarea } from "@omnitok/ui";
import { Callout, FormField } from "@pervasivemind/admin-ui";
import { useCallback, useEffect, useState } from "react";

import { useT } from "@/i18n";
import {
  SCOPE_ID_RE,
  scopesApi,
  type DimensionKeyOut,
  type ScopeRule,
} from "@/lib/access-api";
import {
  ScopeRulesEditor,
  type ReferenceProjects,
} from "@/pages/admin-access/ScopeRulesEditor";

/** Client-side mirror of `ck_scopes_id_shape`. Returns the i18n key of
 *  the reason, or `null` when the id is acceptable. Exported so a test
 *  can walk the boundary cases without a DOM. */
export function scopeIdProblem(raw: string): string | null {
  if (raw === "") return "access.scopes.form.idRequired";
  if (raw !== raw.trim()) return "access.scopes.form.idSpaces";
  if (!SCOPE_ID_RE.test(raw)) return "access.scopes.form.idShape";
  return null;
}

export function ScopeCreateModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}): JSX.Element | null {
  const { t } = useT();
  const [id, setId] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [rules, setRules] = useState<ScopeRule[]>([]);
  const [references, setReferences] = useState<ReferenceProjects>({});
  const [catalogue, setCatalogue] = useState<DimensionKeyOut[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadCatalogue = useCallback(async (): Promise<void> => {
    try {
      setCatalogue((await scopesApi.dimensionKeys()).keys);
    } catch (err) {
      setCatalogue(null);
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setId("");
    setName("");
    setDescription("");
    setRules([]);
    setReferences({});
    setError(null);
    void loadCatalogue();
  }, [open, loadCatalogue]);

  if (!open) return null;

  const idProblem = scopeIdProblem(id);
  const canSave =
    idProblem === null && name.trim() !== "" && rules.length > 0 && !saving;

  const submit = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      const created = await scopesApi.create({
        id,
        name: name.trim(),
        description,
        rules,
      });
      onCreated(created.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t("access.scopes.form.createTitle")}
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button ghost size="sm" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={!canSave}
            onClick={() => void submit()}
          >
            {t("common.create")}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Callout tone="info" title={t("access.scopes.form.grantsNothingTitle")}>
          {t("access.scopes.form.grantsNothing")}
        </Callout>

        {error !== null && (
          <Callout tone="danger" title={t("access.common.errorTitle")}>
            <span className="whitespace-pre-wrap">{error}</span>
          </Callout>
        )}

        <FormField
          label={t("access.scopes.form.id")}
          help={t("access.scopes.form.idHelp")}
          error={id === "" || idProblem === null ? undefined : t(idProblem)}
          required
        >
          <Input
            value={id}
            /* No `toLowerCase()` here, deliberately: the case is part of
               the identity and folding it would create a scope under a
               name the analyst did not type. */
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setId(e.target.value)
            }
            placeholder="us-north"
          />
        </FormField>

        <FormField label={t("access.scopes.form.name")} required>
          <Input
            value={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setName(e.target.value)
            }
          />
        </FormField>

        <FormField
          label={t("access.scopes.form.description")}
          help={t("access.scopes.form.descriptionHelp")}
        >
          <Textarea
            value={description}
            rows={2}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setDescription(e.target.value)
            }
          />
        </FormField>

        <div>
          <p className="mb-2 text-sm font-semibold text-neutral-800">
            {t("access.scopes.form.rulesTitle")}
          </p>
          {catalogue === null ? (
            <p className="text-[13px] text-neutral-500">
              {t("common.loading")}
            </p>
          ) : (
            <ScopeRulesEditor
              catalogue={catalogue}
              rules={rules}
              references={references}
              onChange={setRules}
              onReferenceChange={setReferences}
            />
          )}
        </div>
      </div>
    </Modal>
  );
}
